import { useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { ShareNetwork, Copy, Check, Printer, WhatsappLogo, FacebookLogo, XLogo, LinkedinLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

export default function ShareButton({ targetPath, entityType = "profile", entityId, title, label = "Share", variant = "outline" }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (link) return;
    setLoading(true);
    try {
      const knownPublic = ["institution", "person", "people", "alumni", "article", "memorial", "group", "event", "reunion"];
      if (entityId && knownPublic.includes(entityType)) {
        const { data } = await api.get("/share", { params: { entity_type: entityType, entity_id: entityId } });
        setLink(data);
      } else {
        const { data } = await api.post("/links", { target_path: targetPath, entity_type: entityType, entity_id: entityId, title });
        setLink(data);
      }
    } catch {
      toast.error("Could not create share link");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(link.short_url);
    setCopied(true);
    toast.success("Short link copied!");
    setTimeout(() => setCopied(false), 1500);
  };

  // Direct, current-origin page URL — always opens the correct domain (alumnipulse.net / indiaalumni.com).
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}${targetPath || window.location.pathname}` : "";
  const shareText = title ? `${title} — AlumniPulse` : "AlumniPulse";
  const enc = encodeURIComponent;
  const socials = [
    { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}`, Icon: WhatsappLogo, color: "#25D366" },
    { key: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, Icon: FacebookLogo, color: "#1877F2" },
    { key: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`, Icon: XLogo, color: "#111" },
    { key: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`, Icon: LinkedinLogo, color: "#0A66C2" },
    { key: "email", label: "Email", href: `mailto:?subject=${enc(shareText)}&body=${enc(shareUrl)}`, Icon: EnvelopeSimple, color: "#555" },
  ];

  const printCard = () => {
    const svgUrl = link.qr_code_url ? `${link.qr_code_url}?format=svg` : `${API}/links/${link.code}/qr.svg`;
    const safe = (title || "AlumniPulse").replace(/</g, "&lt;");
    const w = window.open("", "_blank", "width=760,height=980");
    if (!w) { toast.error("Please allow pop-ups to print the card"); return; }
    w.document.write(`<!doctype html><html><head><title>QR Card — ${safe}</title>
      <style>
        @page { size: A6; margin: 10mm; }
        * { box-sizing: border-box; }
        body { font-family: Georgia, 'Times New Roman', serif; margin: 0; display: grid; place-items: center; min-height: 100vh; background: #faf8f3; }
        .card { width: 340px; padding: 28px; text-align: center; border: 2px solid #1f3d33; border-radius: 18px; background: #fff; }
        .brand { font-size: 12px; letter-spacing: 3px; text-transform: uppercase; color: #1f8a5b; margin-bottom: 14px; }
        .name { font-size: 22px; color: #1a2b25; margin: 0 0 4px; line-height: 1.2; }
        .qr { width: 210px; height: 210px; margin: 16px auto; }
        .qr img { width: 100%; height: 100%; }
        .scan { font-size: 13px; color: #55665f; margin: 10px 0 6px; }
        .url { font-family: ui-monospace, monospace; font-size: 13px; color: #1f3d33; word-break: break-all; }
        .foot { margin-top: 16px; font-size: 11px; color: #99a6a0; }
        @media print { body { background: #fff; } .noprint { display: none; } }
        .noprint { margin-top: 18px; }
        button { padding: 8px 18px; border: 1px solid #1f3d33; background: #1f3d33; color: #fff; border-radius: 999px; cursor: pointer; font-family: sans-serif; }
      </style></head><body>
      <div class="card">
        <div class="brand">AlumniPulse</div>
        <h1 class="name">${safe}</h1>
        <div class="qr"><img src="${svgUrl}" alt="QR code" onerror="this.src='${API}/links/${link.code}/qr.png'" /></div>
        <div class="scan">Scan to view &amp; connect on AlumniPulse</div>
        <div class="url">${link.short_url}</div>
        <div class="foot">alumnipulse.net · preserving people behind education</div>
      </div>
      <div class="noprint"><button onclick="window.print()">Print this card</button></div>
      </body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) {} }, 700);
  };

  return (
    <Dialog onOpenChange={(o) => o && generate()}>
      <DialogTrigger asChild>
        <Button variant={variant} size="sm" data-testid="share-button" className="gap-2 rounded-full">
          <ShareNetwork size={16} /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Share</DialogTitle>
          <DialogDescription>Post to social media, or use the branded short link &amp; QR code.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center justify-center gap-2.5 pt-1" data-testid="share-socials">
          {socials.map(({ key, label, href, Icon, color }) => (
            <a key={key} href={href} target="_blank" rel="noreferrer" title={`Share on ${label}`} aria-label={`Share on ${label}`} data-testid={`share-social-${key}`}
              className="grid h-11 w-11 place-items-center rounded-full border border-border/60 transition-colors hover:bg-accent">
              <Icon size={22} weight="fill" style={{ color }} />
            </a>
          ))}
        </div>
        {loading && <p className="text-sm text-muted-foreground py-8 text-center">Generating short link & QR…</p>}
        {link && (
          <div className="space-y-5">
            <div className="grid place-items-center rounded-2xl border border-border/60 bg-muted/40 p-6">
              <img src={link.qr_code_url || `${API}/links/${link.code}/qr.png`} alt="QR code for the share link" className="h-44 w-44 rounded-lg bg-white p-2" data-testid="share-qr" />
            </div>
            <div className="flex items-center gap-2 rounded-full border border-border/60 bg-background py-1.5 pl-4 pr-1.5">
              <span className="flex-1 truncate text-sm font-medium" data-testid="share-url">{link.short_url}</span>
              <Button size="sm" onClick={copy} className="rounded-full gap-1.5" data-testid="share-copy">
                {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <Button variant="outline" onClick={printCard} className="w-full rounded-full gap-2" data-testid="share-print">
              <Printer size={16} /> Print QR card
            </Button>
            <p className="text-center text-xs text-muted-foreground">{link.clicks} clicks · Branded on alumnipulse.net</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
